import {
  ConditionalPaginationProps,
  TableCellProps,
  WbTable,
} from '@agilelab/plugin-wb-platform';
import {
  Box,
  createStyles,
  FormControl,
  makeStyles,
  Typography,
} from '@material-ui/core';
import { useMemo } from 'react';
import { useMultiSelectionPickerContext } from './MultiSelectionPickerContext';
import { GenericElement } from './types';

interface SelectableTableProps<T extends GenericElement> {
  loading: boolean;
  data: T[];
  columns?: TableCellProps<T>[];
  additionalColumns?: TableCellProps<T>[];
  pagination?: ConditionalPaginationProps;
  getRowId: (row: T) => string;
  filtersSection?: () => JSX.Element;
  kindFilter?: string;
}

const useStyles = makeStyles(
  theme =>
    createStyles({
      tableContainer: {
        flex: 1,
        overflowY: 'auto',
      },
      formControl: {
        width: '100%',
        justifyContent: 'space-between',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      },
      boxBest: {
        padding: `${theme.spacing(0.5)}px ${theme.spacing(1)}px`,
        backgroundColor: theme.palette.white,
        border: 'solid 1px transparent',
        borderRadius: '4px',
        background:
          'linear-gradient( #ffffff, #ffffff) padding-box,linear-gradient(90deg, #EE72D3, #7C4DFF) border-box',
        whiteSpace: 'nowrap',
      },
      aiSuggestedRow: {
        backgroundColor: `${theme.palette.bkg.accent} !important`,
        '& td': {
          borderBottom: `1px solid ${theme.palette.accent.light}`,
        },
      },
      tableDisabled: {
        pointerEvents: 'none',
        opacity: 0.5,
      },
    }),
  { name: 'selectionTable' },
);

export function SelectableTable<T extends GenericElement>({
  loading,
  data,
  columns,
  additionalColumns,
  getRowId,
  filtersSection,
  kindFilter,
  pagination,
}: SelectableTableProps<T>) {
  const classes = useStyles();
  const { contextSelected, setContextSelected, isAiGenerated, disableAll } =
    useMultiSelectionPickerContext();

  const filteredContextSelected = useMemo(
    () =>
      kindFilter
        ? contextSelected.filter(item => item.kind === kindFilter)
        : contextSelected,
    [contextSelected, kindFilter],
  );

  const handleSetContextSelected = (newSelection: T[]) => {
    setContextSelected(prevSelection => {
      const prevFiltered = kindFilter
        ? prevSelection.filter(item => item.kind !== kindFilter)
        : [];

      // Get only the new selections that match the kindFilter
      const selectedKindSelection = newSelection.filter(item =>
        kindFilter ? item.kind === kindFilter : true,
      );

      const updatedSelection = [...prevFiltered, ...selectedKindSelection];

      return updatedSelection;
    });
  };

  const defaultColumns = useMemo(() => {
    const cols = [
      {
        headerName: 'Name',
        field: 'name',
        fieldRender: (field: T) => {
          return (
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography>{field.displayName ?? field.name}</Typography>
              {isAiGenerated(field) && (
                <Typography className={classes.boxBest} variant="caption">
                  Best match
                </Typography>
              )}
            </Box>
          );
        },
      },
    ] as TableCellProps<T>[];

    if (additionalColumns) {
      cols.push(...additionalColumns);
    }

    return cols;
  }, [additionalColumns, classes.boxBest, isAiGenerated]);

  return (
    <>
      {filtersSection?.()}
      <FormControl className={classes.formControl}>
        <WbTable
          stickyHeader
          classNames={{
            container: classes.tableContainer,
            body: disableAll ? classes.tableDisabled : '',
            header: disableAll ? classes.tableDisabled : '',
          }}
          selection="multiple"
          selectedItems={filteredContextSelected}
          setSelectedItems={handleSetContextSelected}
          components={{
            tableLoader: { loading },
            tableContent: {
              getRowId,
              columns: columns ?? defaultColumns,
              rows: data,
              rowDynamicClassName: row => {
                return isAiGenerated(row) ? classes.aiSuggestedRow : undefined;
              },
            },
          }}
          pagination={pagination}
        />
      </FormControl>
    </>
  );
}
